import { ApiError, api } from "@/common/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ImportConfirmInput,
  ImportConfirmResult,
  ImportFilePayload,
  ImportPreviewResult,
} from "../types";

const XLSX_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Decide por extensión del filename — File.type viene vacío en algunos
// sistemas, así que no es una señal confiable.
function isXlsxFileName(fileName: string): boolean {
  return fileName.toLowerCase().endsWith(".xlsx");
}

export function useImportPreview(accountId: string) {
  return useMutation<ImportPreviewResult, Error, ImportFilePayload>({
    mutationFn: ({ fileName, content }) =>
      api.postRaw<ImportPreviewResult>(
        `/accounts/${accountId}/animals/import/preview`,
        content,
        isXlsxFileName(fileName) ? XLSX_CONTENT_TYPE : "text/csv",
      ),
  });
}

export function useImportConfirm(accountId: string) {
  const qc = useQueryClient();
  return useMutation<ImportConfirmResult, Error, ImportConfirmInput>({
    mutationFn: (input) =>
      api.post<ImportConfirmResult>(`/accounts/${accountId}/animals/import/confirm`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["animals", accountId] });
    },
  });
}

// Descarga autenticada de la plantilla .xlsx — mismo patrón que
// downloadExportFile del journal: fetch con bearer → blob → <a download>.
// No puede ser un <a href> directo porque el endpoint exige Authorization.
export async function downloadImportTemplate(accountId: string): Promise<void> {
  const { getIdToken } = await import("@/common/lib/auth");
  const token = await getIdToken(false);
  const res = await fetch(`/api/accounts/${accountId}/animals/import/template`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, "DOWNLOAD_FAILED", `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "plantilla-hato-mihato.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
